import LoaderOverlay from "@edaz/edazkit/LoaderOverlay";
import type { ViewComponent } from "@frui.ts/views";
import type VehicleRegisterVehicleClassifiedFuelType from "entities/vehicleRegisterVehicleClassifiedFuelType";
import VehicleRegisterVehiclePropertyChangeDisplayType from "entities/vehicleRegisterVehiclePropertyChangeDisplayType";
import type VehicleRegisterVehicleStatus from "entities/vehicleRegisterVehicleStatus";
import type VehicleRegisterVehicleSynchronizationChangeResponse from "entities/vehicleRegisterVehicleSynchronizationChangeResponse";
import VehicleRegisterVehicleSynchronizationResponse from "entities/vehicleRegisterVehicleSynchronizationResponse";
import { observer } from "mobx-react-lite";
import React from "react";
import { emptyValue } from "utils/emptyValue";
import { formatDate } from "utils/localizations";
import type VehicleSynchronizationViewModel from "viewModels/callCenter/search/vehicleRegister/vehicleSynchronization/vehicleSynchronizationViewModel";
import AlertWithIcon from "views/common/alertWithIcon";

// eslint-disable-next-line sonarjs/cognitive-complexity
export const VehicleSynchronizationSecondStepView: ViewComponent<VehicleSynchronizationViewModel> = observer(({ vm }) => {
  if (!vm.vehicleRegister) {
    return null;
  }

  function changeValueFormatter(item: VehicleRegisterVehicleSynchronizationChangeResponse, type: "new" | "original") {
    const value = type === "new" ? item.newValue : item.originalValue;
    const translateValue = vm.localizationService.translateEnum(
      "vehicleRegisterVehiclePropertyChangeDisplayType",
      value as VehicleRegisterVehiclePropertyChangeDisplayType
    );
    if (!value) {
      return emptyValue;
    }
    if (!translateValue.includes(".")) {
      return translateValue;
    }

    switch (item.changeType) {
      case VehicleRegisterVehiclePropertyChangeDisplayType.ClassifiedFuelTypeChange:
        return vm.translateVehicleRegisterVehicleClassifiedFuelType(value as VehicleRegisterVehicleClassifiedFuelType);

      case VehicleRegisterVehiclePropertyChangeDisplayType.VehicleStatusChange:
        return vm.translateVehicleStatus(value as VehicleRegisterVehicleStatus);

      case VehicleRegisterVehiclePropertyChangeDisplayType.MaximumAllowedWeightChange:
        return vm.formatHeight(Number(value));

      case VehicleRegisterVehiclePropertyChangeDisplayType.FuelTypesChange: {
        return vm.translateVehicleRegisterVehicleFuelType(value);
      }

      case VehicleRegisterVehiclePropertyChangeDisplayType.LicensePlateValidSinceDateChange:
      case VehicleRegisterVehiclePropertyChangeDisplayType.LicensePlateValidUntilDateChange:
      case VehicleRegisterVehiclePropertyChangeDisplayType.VehicleStatusValidSinceDateChange:
      case VehicleRegisterVehiclePropertyChangeDisplayType.VehicleStatusValidUntilDateChange:
        return <span className="text-nowrap">{formatDate(new Date(value))}</span>;

      default:
        return value;
    }
  }

  const changes = (
    <div className="mb-6">
      <span>{vm.translate("changes")}</span>
    </div>
  );

  const noChanges = (
    <div className="mb-6">
      <span>
        {vm.translate("noChanges.firstPart")}
        <strong>{vm.translate("noChanges.secondPart")}</strong>
      </span>
    </div>
  );

  const disabledRSV = (
    <div className="mb-6">
      <AlertWithIcon variant="danger">{vm.translate("disabledRSV")}</AlertWithIcon>
    </div>
  );

  const failedSync = (
    <div className="mb-6">
      <AlertWithIcon variant="danger">{vm.translate("failedSync")}</AlertWithIcon>
    </div>
  );
  return (
    <>
      <LoaderOverlay loading={vm.busyWatcher.isBusy} style={{ minHeight: 50 }}>
        {vm.noChanges && noChanges}
        {vm.disabledRSV && disabledRSV}
        {vm.failedSync && failedSync}
        {vm.changes && changes}
        {!vm.busyWatcher.isBusy && vm.transformedItems?.length > 0 && (
          <table className="table table-tbody-striped tbody-larger">
            <thead>
              <tr>
                <th>{vm.translate("changeType")}</th>
                <th>{vm.translate("originalValue")}</th>
                <th style={{ width: 70 }}></th>
                <th>{vm.translate("newValue")}</th>
              </tr>
            </thead>
            <>
              {vm.transformedItems?.map((item, indexItem) => {
                return (
                  <>
                    <tbody key={`${item.changeType}`}>
                      <tr className={`${(indexItem + 1) % 2 === 0 ? "even" : "odd"}`}>
                        <td>
                          <strong className="text-success">{vm.translateChangeType(item.changeType)}</strong>
                        </td>
                        <td>{item.originalValue ? changeValueFormatter(item, "original") : emptyValue}</td>
                        <td>→</td>
                        <td>{item.newValue ? changeValueFormatter(item, "new") : emptyValue}</td>
                      </tr>
                    </tbody>
                  </>
                );
              })}
            </>
          </table>
        )}
      </LoaderOverlay>
    </>
  );
});

export default VehicleSynchronizationSecondStepView;
VehicleRegisterVehicleSynchronizationResponse;
